// ─────────────────────────────────────────────────────────────────────────────
// Les langues du studio (8 octobre 2026). Le français est la SOURCE ; les
// autres langues s'ajoutent au fur et à mesure.
//
// Un catalogue par ESPACE et par langue : `fr/coquille.ts`, `en/coquille.ts`,
// `fr/radar.ts`… La clé d'un texte commence par son espace
// (`coquille.nav.radar`). Un écran n'écrit que dans ses propres fichiers :
// plusieurs écrans se traduisent en parallèle sans se marcher dessus, et le
// test `catalogues.test.ts` vérifie que CHAQUE langue a chaque clé française.
//
// ── Ajouter une langue ──────────────────────────────────────────────────────
//
//   1. une ligne dans `LANGUES_INFO` ci-dessous (code, nom dans sa langue,
//      locale des dates et des nombres) ;
//   2. un dossier `src/i18n/<code>/` avec tous les espaces du français ;
//   3. pour les textes des modèles : son glossaire dans `@orion/domain`
//      (`GLOSSAIRES` de traduction.ts) et le code dans `LANGUES_TRADUITES`.
// Le choix de langue de la barre latérale et de la connexion suit cette liste.
//
// Ce qui est ici, c'est l'INTERFACE. Les textes écrits par les modèles passent
// par `useTraduction` (composables/traduction.composable.ts).
// ─────────────────────────────────────────────────────────────────────────────
import { createI18n } from 'vue-i18n'
import { eliderLesDe } from './elision'

export const LANGUES_INFO = [
  { code: 'fr', nom: 'Français', intl: 'fr-FR' },
  // Anglais BRITANNIQUE : « 8 October 2026 », le jour avant le mois, comme le
  // lit un client européen.
  { code: 'en', nom: 'English', intl: 'en-GB' },
] as const

export type Langue = (typeof LANGUES_INFO)[number]['code']
export const LANGUES: readonly Langue[] = LANGUES_INFO.map((l) => l.code)
/** La langue dans laquelle tout est écrit d'abord, et vers laquelle on retombe. */
export const LANGUE_SOURCE: Langue = 'fr'
export const estLangue = (v: unknown): v is Langue => typeof v === 'string' && (LANGUES as readonly string[]).includes(v)

type Catalogue = Record<string, unknown>
type Modules = Record<string, { default: Catalogue }>

/** `./en/coquille.ts` → `{ en: { coquille: … } }`, pour toutes les langues présentes. */
export function rassembler(modules: Modules): Record<string, Record<string, Catalogue>> {
  const out: Record<string, Record<string, Catalogue>> = {}
  for (const [chemin, m] of Object.entries(modules)) {
    const r = /^.*\/([^/]+)\/([^/]+)\.ts$/.exec(chemin)
    if (r === null) continue
    const [, langue, espace] = r as unknown as [string, string, string]
    ;(out[langue] ??= {})[espace] = m.default
  }
  return out
}

export const catalogues = rassembler(import.meta.glob<{ default: Catalogue }>('./*/*.ts', { eager: true }))

export const i18n = createI18n({
  legacy: false,
  locale: LANGUE_SOURCE,
  // Un texte pas encore traduit s'affiche en français plutôt qu'en clé brute.
  fallbackLocale: LANGUE_SOURCE,
  messages: catalogues,
  missingWarn: false,
  fallbackWarn: false,
  // « Les électeurs de {nom} » avec « Édouard Philippe » : « d’Édouard » (elision.ts).
  postTranslation: (texte) => (typeof texte === 'string' ? eliderLesDe(texte) : texte),
})

/** La langue courante, lue partout où un `useI18n` n'est pas à portée. */
export const langueCourante = (): Langue => {
  const l = i18n.global.locale.value
  return estLangue(l) ? l : LANGUE_SOURCE
}

/** La locale des formats de date et de nombre de la langue courante. */
export const localeIntl = (): string => LANGUES_INFO.find((l) => l.code === langueCourante())?.intl ?? 'fr-FR'
